// V2-004 — Cart slide-out drawer + add-to-cart toast.
//
// Hijacks all <form action="/cart/add"> submissions. On success:
//   1. Show toast "S-a adăugat în coș".
//   2. Bump the header cart count (animated).
//   3. Open the slide-out drawer.
//   4. Lazy-fetch /cart/?_drawer=1 → drawer body.
//
// Falls back to a normal navigation on fetch failure (so the existing
// cart page still works).

const FREE_SHIPPING_DEFAULT = 200; // lei — overridable via [data-threshold]
const TOAST_DURATION = 3500;

function showToast(text: string) {
  const t = document.createElement("div");
  t.className = "v2-toast";
  t.textContent = text;
  document.body.appendChild(t);
  setTimeout(() => {
    t.classList.add("is-leaving");
    setTimeout(() => t.remove(), 220);
  }, TOAST_DURATION);
}

function bumpCartCount(delta = 1) {
  const el = document.querySelector<HTMLElement>("[data-line-count]");
  if (!el) return;
  const current = parseInt(el.textContent || "0", 10) || 0;
  el.textContent = String(current + delta);
  el.style.transition = "transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)";
  el.style.transform = "scale(1.4)";
  setTimeout(() => { el.style.transform = "none"; }, 240);
}

async function refreshDrawerBody() {
  const drawer = document.getElementById("v2CartDrawer");
  if (!drawer) return;
  const body   = drawer.querySelector<HTMLElement>("[data-drawer-body]");
  const footer = drawer.querySelector<HTMLElement>("[data-drawer-footer]");
  const subtotalEl = drawer.querySelector<HTMLElement>("[data-drawer-subtotal]");
  const lineCountEl = drawer.querySelector<HTMLElement>("[data-drawer-line-count]");
  const fsBlock = drawer.querySelector<HTMLElement>("[data-free-shipping]");
  if (!body) return;

  try {
    const res = await fetch("/cos/?_drawer=1", {
      headers: { "Accept": "application/x-html-fragment" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    body.innerHTML = html;
    body.style.padding = "0";

    // Compute subtotal + line count from the inserted DOM (look for
    // [data-cart-subtotal] / [data-cart-line-count] hooks; fall back
    // to the existing cart-summary span format if not present).
    const subtotal = parseFloat(body.querySelector<HTMLElement>("[data-cart-subtotal]")?.dataset.value || "0");
    const lines    = parseInt(body.querySelector<HTMLElement>("[data-cart-line-count]")?.dataset.value || "0", 10);
    if (subtotalEl) subtotalEl.textContent = subtotal.toFixed(2).replace(".", ",") + " lei";
    if (lineCountEl) lineCountEl.textContent = lines > 0 ? `(${lines} ${lines === 1 ? "produs" : "produse"})` : "";
    if (footer) footer.hidden = lines === 0;

    // Update free-shipping progress.
    if (fsBlock && lines > 0) {
      const threshold = parseFloat(fsBlock.dataset.threshold || String(FREE_SHIPPING_DEFAULT));
      const remaining = Math.max(0, threshold - subtotal);
      const pct = Math.min(100, Math.round((subtotal / threshold) * 100));
      const msgEl = fsBlock.querySelector<HTMLElement>("[data-fs-message]");
      const barEl = fsBlock.querySelector<HTMLElement>("[data-fs-bar]");
      if (remaining > 0) {
        if (msgEl) msgEl.innerHTML = `Mai ai <strong>${remaining.toFixed(2).replace(".", ",")} lei</strong> pentru <strong>livrare gratuită</strong>.`;
      } else {
        if (msgEl) msgEl.innerHTML = `🎉 <strong>Beneficiezi de livrare gratuită!</strong>`;
      }
      if (barEl) barEl.style.width = pct + "%";
      fsBlock.hidden = false;
    }
  } catch (err) {
    console.warn("[cart] drawer refresh failed", err);
  }
}

function attach() {
  // 1. Hijack add-to-cart forms.
  document.addEventListener("submit", async (e) => {
    const form = e.target as HTMLFormElement;
    if (!form?.matches('form[action="/cart/add"]')) return;
    e.preventDefault();

    const fd = new FormData(form);
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) { submit.disabled = true; submit.style.opacity = "0.65"; }

    try {
      const res = await fetch(form.action, {
        method: "POST",
        body: fd,
        headers: { "X-CSRFToken": fd.get("csrf_token") as string || "" },
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Success path.
      showToast("S-a adăugat în coș");
      const qty = parseInt(String(fd.get("qty") || "1"), 10) || 1;
      bumpCartCount(qty);
      // Open drawer + fetch its body.
      const drawerEl = document.getElementById("v2CartDrawer");
      if (drawerEl && (window as any).bootstrap) {
        const off = (window as any).bootstrap.Offcanvas.getOrCreateInstance(drawerEl);
        off.show();
        refreshDrawerBody();
      } else {
        // No drawer markup (older page) — fall back to navigation.
        location.assign("/cart/");
      }
    } catch (err) {
      console.warn("[cart] add failed, falling back to navigation", err);
      form.submit();
    } finally {
      if (submit) { submit.disabled = false; submit.style.opacity = "1"; }
    }
  });

  // 2. Refresh drawer body whenever it opens (handles the case where the
  // user clicks a header cart icon directly without an add-to-cart action).
  document.getElementById("v2CartDrawer")?.addEventListener("show.bs.offcanvas", refreshDrawerBody);
}

if (document.readyState !== "loading") attach();
else document.addEventListener("DOMContentLoaded", attach);
